import { DateTime } from 'luxon';
import { isMobileOnly } from 'react-device-detect';
import { ItemToReturn } from '@src/models/returns';
import { RETURN_METHOD, ReturnMethodType } from '../constants/general';
import { getCookieValue } from './cookie-helper';
import {
  FlowStep,
  TStateManagementFiltered,
  TStateManagementResponse,
  TTrialsObject,
  TTrialsResponse,
} from '../models/common';
import {
  FlowDropOffBoxesStep,
  FlowFinalizeReturnStep,
  FlowItemsToReturnStep,
  FlowLORPContactDetailsStep,
  FlowLORPUserAcknowledgeStep,
  FlowLORPWhatToExpectStep,
  FlowRefundMethodStep,
  FlowReturnMethodStep,
} from '../constants';

// set of protected feature switches not allowed to be overwritten by a cookie
// (in prod). enableROS is the ROS migration switch — protect it so a cookie
// cannot enable the v2 path in prod before the BE endpoint is live.
const PROTECTED_FS_SET = new Set(['boxesPerItem', 'enableROS']);

interface AppInstance {
  expConfiguration?: Record<string, string>;
  [key: string]: unknown;
}

export const isFeatureSwitchEnabled = (
  instance: AppInstance = {},
  featureSwitchName = '',
) => {
  if (!instance) return false;
  const validTrueInput = new Set(['true', '1']);
  const validFalseInput = new Set(['false', '0']);

  const isProd = window.location.hostname === 'www.homedepot.com';
  const isFSProtected = isProd && PROTECTED_FS_SET.has(featureSwitchName);

  if (!isFSProtected) {
    // Check cookies first
    const cookieValue = getCookieValue(featureSwitchName);
    if (cookieValue !== undefined) {
      if (validTrueInput.has(cookieValue)) return true;
      if (validFalseInput.has(cookieValue)) return false;
    }
  }

  const featureSwitchValue =
    instance?.expConfiguration?.[featureSwitchName] ?? '';
  return validTrueInput.has(featureSwitchValue);
};

// Used when instance variable is not available in non-hooks
export const getFeatureSwitchStatic = (
  featureSwitchName: string,
  isBoolean = true,
  serverDefault = false,
) => {
  // if true, will return true in initial server rendering. Otherwise getWindow() will fail and return false.
  if (!getWindow() && serverDefault) return serverDefault;
  if (!getWindow()) return false;
  const validTrueInput = new Set(['true', '1']);
  const validFalseInput = new Set(['false', '0']);

  const isProd = window?.location.hostname === 'www.homedepot.com';
  const isFSProtected = isProd && PROTECTED_FS_SET.has(featureSwitchName);

  if (!isFSProtected) {
    // Check cookies first
    const cookieValue = getCookieValue(featureSwitchName);
    if (cookieValue !== undefined) {
      if (isBoolean) {
        if (validTrueInput.has(cookieValue)) return true;
        if (validFalseInput.has(cookieValue)) return false;
      }
      return cookieValue;
    }
  }
  const featureSwitchValue =
    window?.__EXPERIENCE_PROPS__?.expConfiguration?.[featureSwitchName];
  if (isBoolean) return validTrueInput.has(featureSwitchValue);
  return featureSwitchValue;
};

export const isSsr = typeof window === 'undefined';

export const getHost = (): string => {
  if (isSsr) return '';

  const isLocalDev = window.location.hostname.startsWith('localhost');
  if (
    process.env.NODE_ENV === 'production' &&
    !process.env.DOMAIN_NAME &&
    !isLocalDev
  ) {
    return `https://${window.location.hostname}`;
  }

  return window.location.origin;
};

export const getChannelID = (): number => (isMobileOnly ? 2 : 1);

export const generateXB3TraceID = () =>
  Math.random().toString(16).substring(2) +
  new Date().getTime().toString(16).substring(0, 15);

// CorrelationId used to correlate multiple requests sent during the same session
export const SESSION_CORRELATION_ID = generateXB3TraceID();

export const getUrlParam = (key: string) => {
  const searchParams =
    typeof window !== 'undefined' ? window?.location?.search : '';
  const urlParams = new URLSearchParams(searchParams);
  return urlParams.get(key);
};

export const getTmxId = (): string => {
  let nativeTMXID: string | null = '';
  if (window.__EXPERIENCE_CONTEXT__?.isConsumerApp) {
    nativeTMXID = getUrlParam('guid');
  }
  return nativeTMXID || window.THD_ACCOUNT_LIB?.TMX?.getTMXsessionId();
};

/**
 * Functions used for userState, policy, trails and enrollment
 * set for left nav within server.js
 */
export const transformToTrialsObj = ({
  data,
}: {
  data: TTrialsResponse;
}): TTrialsObject => {
  const trials: TTrialsObject = {};
  data?.features?.forEach((feature) => {
    trials[feature] = true;
  });
  return trials;
};

export const filterOutPII = ({
  data,
}: {
  data: TStateManagementResponse;
}): TStateManagementFiltered => {
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    firstName,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    lastName,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    phoneNumber,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    email,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    userAddress,
    customer,
    ...needed
  } = data;

  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    phoneNumber: customerPhone,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    accountName,
    ...neededFromCustomer
  } = customer;
  return { ...needed, customer: { ...neededFromCustomer } };
};

// Checks if the current environment is not SSR.
export const getWindow = (): Window | undefined => {
  if (typeof window !== 'undefined') {
    return window;
  }
  return undefined;
};

/**
 * Returns a simplified date string in the format: Month Day, Year.
 *
 * @param date - The date string in ISO 8601 format.
 * @returns The simplified date string.
 */
export const getSimplifiedDate = (date: string) => {
  if (!date) return '';
  const dateValue = new Date(`${date}T00:00:00Z`);
  const simplifiedDate = dateValue.toLocaleDateString('en-US', {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  return simplifiedDate;
};

export const formatPickupDate = (estPickupDate: string) => {
  const startDate = DateTime.fromISO(estPickupDate);
  const today = DateTime.local().startOf('day');
  const tomorrow = today.plus({ days: 1 });

  let prefix = '';
  if (startDate.hasSame(today, 'day')) {
    prefix = 'Today';
  } else if (startDate.hasSame(tomorrow, 'day')) {
    prefix = 'Tomorrow';
  }

  return {
    prefix,
    dayOfWeek: startDate.toFormat('ccc'),
    monthDay: startDate.toFormat('LLL d'),
    shortFullDate: `${prefix ? prefix + ', ' : ''}${startDate.toFormat(
      'ccc LLL d',
    )}`,
    longFullDate: `${prefix ? prefix + ', ' : ''}${startDate.toFormat(
      'cccc LLLL d',
    )}`,
    modifiedLongFullDate: `${startDate.toFormat('cccc')}, ${startDate.toFormat(
      'LLLL d',
    )}`,
  };
};

export const roundValueAndTrim = (value: number, decimalPlaces = 3) => {
  return value.toFixed(decimalPlaces).replace(/\.?0+$/, '');
};

export const getStep = (currentStep: FlowStep) => {
  switch (currentStep) {
    case 'ReturnMethod':
      return FlowReturnMethodStep;
    case 'ItemsToReturn':
      return FlowItemsToReturnStep;
    case 'RefundMethod':
      return FlowRefundMethodStep;
    case 'DropOffBoxes':
      return FlowDropOffBoxesStep;
    case 'LORPContactDetails':
      return FlowLORPContactDetailsStep;
    case 'LORPWhatToExpect':
      return FlowLORPWhatToExpectStep;
    case 'LORPUserAcknowledge':
      return FlowLORPUserAcknowledgeStep;
    default:
      return FlowReturnMethodStep;
  }
};
export const getNextStep = (
  currentStep: FlowStep,
  returnMethod?: ReturnMethodType,
) => {
  switch (currentStep) {
    case 'ReturnMethod':
      return FlowItemsToReturnStep;
    case 'ItemsToReturn':
      return FlowDropOffBoxesStep;
    case 'DropOffBoxes':
      return FlowRefundMethodStep;
    case 'RefundMethod':
      if (returnMethod === RETURN_METHOD.LORP)
        return FlowLORPContactDetailsStep;
      return FlowFinalizeReturnStep;
    case 'LORPContactDetails':
      return FlowLORPWhatToExpectStep;
    case 'LORPWhatToExpect':
      return FlowLORPUserAcknowledgeStep;
    case 'LORPUserAcknowledge':
      return FlowFinalizeReturnStep;
    default:
      return FlowReturnMethodStep;
  }
};
export const getPreviousStep = (
  currentStep: FlowStep,
  returnMethod?: ReturnMethodType,
) => {
  switch (currentStep) {
    case 'ReturnMethod':
      return FlowReturnMethodStep;
    case 'ItemsToReturn':
      return FlowReturnMethodStep;
    case 'DropOffBoxes':
      return FlowItemsToReturnStep;
    case 'RefundMethod':
      return FlowDropOffBoxesStep;
    case 'LORPContactDetails':
      return FlowRefundMethodStep;
    case 'LORPWhatToExpect':
      return FlowLORPContactDetailsStep;
    case 'LORPUserAcknowledge':
      return FlowLORPWhatToExpectStep;
    case 'FinalizeReturn':
      if (returnMethod === RETURN_METHOD.LORP)
        return FlowLORPUserAcknowledgeStep;
      return FlowRefundMethodStep;
    default:
      return FlowReturnMethodStep;
  }
};

/**
 * Returns all the text that a component renders. Rarely used, only for getting analytics error message since we return a component, not a string.
 * Has a try catch just in case it errors something out and we don't want to break the page.
 * @param component - The component to extract text from.
 * @returns The text from the component.
 */
export const getTextFromComponent = (component: Component): string => {
  try {
    return extractTextFromComponent(component);
  } catch (error) {
    return '';
  }
};
interface Component {
  props: {
    children: string | Component | (string | Component)[];
  };
}
function extractTextFromComponent(component: Component): string {
  let text = '';

  if (component?.props?.children) {
    const children = Array.isArray(component.props.children)
      ? component.props.children
      : [component.props.children];

    children.forEach((child) => {
      if (typeof child === 'string') {
        text += child;
      } else if (typeof child === 'object' && child.props) {
        text += extractTextFromComponent(child);
      }
    });
  }

  return text;
}

export const triggerQualtricsSurvey = () =>
  setTimeout(() => {
    if (window?.QSI && window?.QSI?.API) {
      window?.QSI?.API.load();
      window?.QSI?.API.run();
    }
  }, 3000);

export const redirectToOrderDetails = (
  orderDetailsPath: string,
  isGuest: boolean,
  replaceHistory = false,
) => {
  const url = isGuest ? '/myaccount/guest-order-tracking' : orderDetailsPath;

  if (replaceHistory) {
    window.location.replace(url);
  } else {
    window.location.href = url;
  }
};

// Format a phone number to the format (XXX) XXX-XXXX as user types it into the input
export const formatPhoneNumber = (phoneNumberDigits?: string) => {
  if (!phoneNumberDigits) return '';
  // Remove all non-digit characters
  const numbers = phoneNumberDigits.replace(/\D/g, '').slice(0, 10);

  const len = numbers.length;

  if (len < 4) return numbers;
  if (len < 7) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
  return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6)}`;
};

export const scrollToTop = (ref?: React.RefObject<HTMLDivElement>) => {
  if (!ref) window.scrollTo(0, 0);
  else ref?.current?.scrollTo(0, 0);
};

export const dateFormat = (
  date: string | undefined,
  format: string,
  localTimezone?: boolean,
) => {
  if (date === undefined || date === null || date === '') return '';

  let dateTime = DateTime.fromISO(date, { zone: 'utc' });
  if (localTimezone) {
    const localZone = DateTime.local().zoneName;
    dateTime = dateTime.setZone(localZone);
  }

  return dateTime
    .toFormat(format)
    .replace('Tue', 'Tues')
    .replace('Thu', 'Thurs');
};

export const getUniqueMethods = (items: ItemToReturn[]) => {
  const uniqueMethods = new Set<ReturnMethodType>();
  items.forEach((item) => {
    if (item.returnMethod) {
      uniqueMethods.add(item.returnMethod);
    }
  });
  return Array.from(uniqueMethods);
};

export const getTotalWeight = (selectedItems: ItemToReturn[]) => {
  return selectedItems.reduce((total, item) => {
    return total + (item.itemWeight || 0) * item.returnQuantity;
  }, 0);
};

export const getTotalUPSWeight = (selectedItems: ItemToReturn[]) => {
  return selectedItems.reduce((total, item) => {
    if (item.returnMethod !== RETURN_METHOD.UPS_DROP_OFF) {
      return total;
    }
    return total + (item.itemWeight || 0) * item.returnQuantity;
  }, 0);
};

export const getLineTotal = (unitPrice = 0, quantity = 0) =>
  unitPrice * quantity;

/**
 * Total value of a single line being returned: unit price × return quantity.
 * Excludes tax, discounts and shipping — the flow holds no such figure, only
 * `itemCost` (OMS unit price, via the O&P handover).
 */
export const getItemLineTotal = (item: ItemToReturn) =>
  getLineTotal(item.itemCost, item.returnQuantity);

/**
 * Total value going back by each return method, keyed by that method, in integer
 * cents.
 *
 * Cents so two methods worth the same amount compare as exactly equal. Summing
 * decimal line totals in float does not, which would let rounding noise settle a
 * draw that getHighestValueMethod's candidate order is meant to settle.
 *
 * Not exported — the cents unit is a footgun outside that comparison.
 */
const getValueTotalsByMethod = (
  items: ItemToReturn[],
): Partial<Record<ReturnMethodType, number>> =>
  items.reduce<Partial<Record<ReturnMethodType, number>>>((totals, item) => {
    const { returnMethod } = item;
    if (!returnMethod) return totals;
    return {
      ...totals,
      [returnMethod]:
        (totals[returnMethod] || 0) + Math.round(getItemLineTotal(item) * 100),
    };
  }, {});

/**
 * Ranks candidates by the value going back by each one's return method and
 * returns the highest, so a multi-method return can be reported to the fraud
 * service once — that record accepts a single reference and no more
 * (MYACCR-2754).
 *
 * Ranking only: pass only candidates that came back with a usable return
 * reference. In-Store and APO/FPO never produce one, and neither does a method
 * that came back as already submitted. MYACCR-2755 owns that gate.
 *
 * Equal totals resolve to the first candidate given, so pass candidates in
 * submission order. Returns `undefined` when `candidates` is empty.
 */
export const getHighestValueMethod = <T extends { type: ReturnMethodType }>(
  items: ItemToReturn[],
  candidates: T[],
): T | undefined => {
  const totals = getValueTotalsByMethod(items);

  return candidates.reduce<T | undefined>(
    (best, candidate) =>
      !best || (totals[candidate.type] || 0) > (totals[best.type] || 0)
        ? candidate
        : best,
    undefined,
  );
};
